/**
 * DOM Text Highlights
 *
 * Safely applies chat highlights to rendered message text.
 *
 * Primary responsibilities:
 * - Find stable text offsets in rendered message DOM
 * - Wrap only text-node slices so block elements keep their structure
 *
 * Dependencies:
 * - Browser DOM APIs
 */

export type TextHighlightInput = {
  text: string
  startOffset: number | null
  endOffset: number | null
  prefix?: string
  suffix?: string
}

export type TextHighlightRange = {
  startOffset: number
  endOffset: number
}

type TextNodeSegment = {
  node: Text
  text: string
  start: number
  end: number
}

type TextHighlightMatch = TextHighlightRange & {
  segments: TextNodeSegment[]
}

export type TextNodeCollectOptions = {
  skipSelectors?: string[]
  skipTags?: string[]
}

export type TextHighlightMarkOptions = {
  attributes?: Record<string, string>
  className: string
}

const DEFAULT_SKIPPED_TAGS = [
  'BUTTON',
  'INPUT',
  'SELECT',
  'STYLE',
  'SCRIPT',
  'TEXTAREA'
]

// --------------

function shouldUseTextNode(node: Text, options: TextNodeCollectOptions = {}) {
  const parent = node.parentElement
  if (!parent || !node.textContent) return false

  const skippedTags = options.skipTags ?? DEFAULT_SKIPPED_TAGS
  if (skippedTags.includes(parent.tagName)) return false

  const skipSelector = (options.skipSelectors ?? []).filter(Boolean).join(',')
  if (skipSelector && parent.closest(skipSelector)) return false

  return true
}

// --------------

function collectTextNodes(container: HTMLElement, options: TextNodeCollectOptions = {}) {
  const walker = document.createTreeWalker(container, NodeFilter.SHOW_TEXT)
  const nodes: Text[] = []
  let currentNode = walker.nextNode()

  while (currentNode) {
    if (currentNode instanceof Text && shouldUseTextNode(currentNode, options)) {
      nodes.push(currentNode)
    }

    currentNode = walker.nextNode()
  }

  return nodes
}

// --------------

function getTextNodeSegments(
  container: HTMLElement,
  options: TextNodeCollectOptions = {}
) {
  let offset = 0

  return collectTextNodes(container, options).map((node) => {
    const text = node.textContent ?? ''
    const segment = {
      node,
      text,
      start: offset,
      end: offset + text.length
    }
    offset += text.length

    return segment
  })
}

// --------------

export function getTextContentForHighlight(
  container: HTMLElement,
  options: TextNodeCollectOptions = {}
) {
  return getTextNodeSegments(container, options)
    .map((segment) => segment.text)
    .join('')
}

// --------------

function findTextBoundaryOffset(segments: TextNodeSegment[], node: Node, offset: number) {
  if (!(node instanceof Text)) return null

  const segment = segments.find((candidate) => candidate.node === node)
  if (!segment) return null

  return segment.start + Math.min(offset, segment.text.length)
}

// --------------

export function getDomRangeOffsets(
  container: HTMLElement,
  range: Range,
  selectedText: string,
  options: TextNodeCollectOptions = {}
): TextHighlightRange | null {
  const segments = getTextNodeSegments(container, options)
  const rawStartOffset = findTextBoundaryOffset(
    segments,
    range.startContainer,
    range.startOffset
  )
  const rawEndOffset = findTextBoundaryOffset(
    segments,
    range.endContainer,
    range.endOffset
  )

  if (rawStartOffset === null || rawEndOffset === null) return null
  if (rawEndOffset <= rawStartOffset) return null

  const fullText = segments.map((segment) => segment.text).join('')
  const rawSelectionText = fullText.slice(rawStartOffset, rawEndOffset)
  const trimmedStart = Math.max(0, rawSelectionText.indexOf(selectedText))
  const startOffset = rawStartOffset + trimmedStart
  const endOffset = startOffset + selectedText.length

  if (endOffset > rawEndOffset) return null

  return { startOffset, endOffset }
}

// --------------

function findHighlightTextOffset(fullText: string, highlight: TextHighlightInput) {
  const prefix = highlight.prefix ?? ''
  const suffix = highlight.suffix ?? ''
  let startOffset = fullText.indexOf(highlight.text)

  while (startOffset >= 0) {
    const endOffset = startOffset + highlight.text.length
    const prefixMatches =
      !prefix || fullText.slice(0, startOffset).endsWith(prefix)
    const suffixMatches =
      !suffix || fullText.slice(endOffset).startsWith(suffix)

    if (prefixMatches && suffixMatches) {
      return startOffset
    }

    startOffset = fullText.indexOf(highlight.text, startOffset + 1)
  }

  return fullText.indexOf(highlight.text)
}

// --------------

function findTextHighlightMatch(
  container: HTMLElement,
  highlight: TextHighlightInput,
  options: TextNodeCollectOptions = {}
): TextHighlightMatch | null {
  const segments = getTextNodeSegments(container, options)
  const fullText = segments.map((segment) => segment.text).join('')
  const savedStartOffset = highlight.startOffset
  const savedEndOffset = highlight.endOffset
  const hasOffsets =
    savedStartOffset !== null &&
    savedEndOffset !== null &&
    savedEndOffset > savedStartOffset

  if (hasOffsets) {
    const rangeText = fullText.slice(savedStartOffset, savedEndOffset)
    if (rangeText === highlight.text) {
      return {
        segments,
        startOffset: savedStartOffset,
        endOffset: savedEndOffset
      }
    }
  }

  const startOffset = findHighlightTextOffset(fullText, highlight)
  if (startOffset < 0) return null

  return {
    segments,
    startOffset,
    endOffset: startOffset + highlight.text.length
  }
}

// --------------

function createHighlightMark(options: TextHighlightMarkOptions) {
  const mark = document.createElement('mark')
  mark.className = options.className

  for (const [name, value] of Object.entries(options.attributes ?? {})) {
    mark.setAttribute(name, value)
  }

  return mark
}

// --------------

function wrapTextNodeSlice(
  node: Text,
  startOffset: number,
  endOffset: number,
  options: TextHighlightMarkOptions
) {
  const text = node.textContent ?? ''
  const parent = node.parentNode
  if (!parent || startOffset >= endOffset) return

  const before = text.slice(0, startOffset)
  const match = text.slice(startOffset, endOffset)
  const after = text.slice(endOffset)
  const mark = createHighlightMark(options)
  mark.textContent = match

  if (before) parent.insertBefore(document.createTextNode(before), node)
  parent.insertBefore(mark, node)
  if (after) parent.insertBefore(document.createTextNode(after), node)
  parent.removeChild(node)
}

// --------------

function wrapTextHighlightMatch(
  match: TextHighlightMatch,
  options: TextHighlightMarkOptions
) {
  const targetSegments = match.segments.filter(
    (segment) => match.startOffset < segment.end && match.endOffset > segment.start
  )

  for (const segment of targetSegments.reverse()) {
    wrapTextNodeSlice(
      segment.node,
      Math.max(0, match.startOffset - segment.start),
      Math.min(segment.text.length, match.endOffset - segment.start),
      options
    )
  }
}

// --------------

export function wrapTextHighlight(
  container: HTMLElement,
  highlight: TextHighlightInput,
  markOptions: TextHighlightMarkOptions,
  collectOptions: TextNodeCollectOptions = {}
) {
  if (!highlight.text.trim()) return false

  const match = findTextHighlightMatch(container, highlight, collectOptions)
  if (!match) return false

  wrapTextHighlightMatch(match, markOptions)
  return true
}

// --------------

export function unwrapTextHighlightMarks(container: HTMLElement, selector: string) {
  container.querySelectorAll(selector).forEach((mark) => {
    const parent = mark.parentNode
    if (!parent) return

    while (mark.firstChild) {
      parent.insertBefore(mark.firstChild, mark)
    }

    parent.removeChild(mark)
    parent.normalize()
  })
}
